import { useMemo, useState } from 'react';
import { Modal, Button, Spin, Alert, message, Tabs, List } from 'antd';
import { CopyOutlined, DownloadOutlined } from '@ant-design/icons';
import {
  REQUIRED_PROFORMA_BILLING_JSON_TEMPLATE,
  PROFORMA_BILLING_JSON_CARRY_FORWARD_NOTES,
  PROFORMA_BILLING_JSON_SECTION_HELP
} from './proformaBillingJsonTemplate';

// ── Stage 6Q — Proforma Billing JSON viewer (READ-ONLY) ───────────────
// Three tabs, all read-only — never mutates anything:
//   1. Saved JSON      — the raw GET /api/erp/sales/proformas/:id response
//                        (header + lines + snapshots + controls + overrides +
//                        remarks + nesting + LAMI/DGU config + totals + audit).
//   2. Required Format — the documented future-safe superset template (static;
//                        from docs/analysis/proforma-billing-json-structure-analysis.md).
//   3. Carry Forward   — short carry-forward rules so nothing is lost later.
// This is a developer/compare aid; it does NOT change save/load logic.

const PRE_STYLE = {
  margin: 0, padding: 16, fontSize: 12, lineHeight: 1.55,
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
  color: '#e2e8f0', background: '#0f172a', whiteSpace: 'pre', overflow: 'auto', minHeight: 120
};

function copyText(text) {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const ta = document.createElement('textarea');
      ta.value = text; ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
    message.success('Copied to clipboard');
  } catch (_e) {
    message.error('Could not copy');
  }
}

function downloadText(text, filename) {
  try {
    const blob = new Blob([text], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (_e) {
    message.error('Could not download');
  }
}

export default function ProformaJsonModal({ open, title, data, loading, error, proformaNo, onClose }) {
  const [activeTab, setActiveTab] = useState('saved');

  const savedJson = useMemo(() => (data != null ? JSON.stringify(data, null, 2) : ''), [data]);
  const requiredJson = useMemo(() => JSON.stringify(REQUIRED_PROFORMA_BILLING_JSON_TEMPLATE, null, 2), []);
  const notesText = useMemo(() => PROFORMA_BILLING_JSON_CARRY_FORWARD_NOTES.map((n, i) => `${i + 1}. ${n}`).join('\n'), []);
  const slug = (proformaNo || 'proforma').replace(/[^a-zA-Z0-9_-]+/g, '_');

  // Copy / Download act on the active tab's content.
  const active = activeTab === 'saved'
    ? { text: savedJson, file: `${slug}-saved.json`, ready: !!data && !loading && !error }
    : activeTab === 'required'
      ? { text: requiredJson, file: 'proforma-required-billing-format.json', ready: true }
      : { text: notesText, file: `${slug}-carry-forward-notes.txt`, ready: true };

  const savedTab = (
    loading ? (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 56, background: '#fff' }}>
        <Spin tip="Loading saved JSON…" />
      </div>
    ) : error ? (
      <Alert type="error" showIcon message="Failed to load JSON" description={error} style={{ margin: 16 }} />
    ) : (
      <pre style={PRE_STYLE}>{savedJson}</pre>
    )
  );

  const requiredTab = (
    <div>
      <Alert
        type="info" showIcon style={{ margin: 12 }}
        message="Display-only reference template"
        description="Future-safe superset documented in docs/analysis/proforma-billing-json-structure-analysis.md. This is NOT a save payload and does not change current save/load logic."
      />
      <pre style={PRE_STYLE}>{requiredJson}</pre>
    </div>
  );

  const notesTab = (
    <div style={{ padding: 12 }}>
      <List
        size="small"
        bordered
        dataSource={PROFORMA_BILLING_JSON_CARRY_FORWARD_NOTES}
        renderItem={(note, i) => (
          <List.Item>
            <span className="text-[12px] text-slate-700">
              <span className="font-semibold text-slate-400">{i + 1}.</span> {note}
            </span>
          </List.Item>
        )}
      />
    </div>
  );

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      width={1040}
      maskClosable
      destroyOnClose
      footer={[
        <Button key="copy" icon={<CopyOutlined />} disabled={!active.ready} onClick={() => copyText(active.text)}>
          Copy
        </Button>,
        <Button key="download" icon={<DownloadOutlined />} disabled={!active.ready} onClick={() => downloadText(active.text, active.file)}>
          Download
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>
      ]}
      styles={{ body: { maxHeight: '72vh', overflow: 'auto', padding: 0 } }}
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        size="small"
        tabBarStyle={{ paddingInline: 12, marginBottom: 0 }}
        items={[
          { key: 'saved', label: 'Saved JSON', children: savedTab },
          { key: 'required', label: 'Required Format', children: requiredTab },
          { key: 'notes', label: 'Carry Forward Notes', children: notesTab }
        ]}
      />
    </Modal>
  );
}

// Re-exported so the section-help map travels with the viewer if needed elsewhere.
export { PROFORMA_BILLING_JSON_SECTION_HELP };
